import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { fmtDateTime, type AgentProposalDto } from '@immoaktas/shared';
import { api } from '../lib/api';
import { useNotify } from '../lib/hooks';
import { Button, ConfirmDialog, Icon, Pill, type PillTone } from '../ui';
import { refreshData, upsertProposal, useAgentUi } from './useAgent';
import { FieldInput, showField, type FieldValue } from './proposals/fields';
import { viewFor } from './proposals/registry';

const TONE: Record<string, PillTone> = { pending: 'warn', applying: 'info', applied: 'ok', rejected: 'neutral', failed: 'err', undone: 'neutral' };

/**
 * A proposal of the assistant (docs/17 §Vorschläge): what it would enter, rows marked "Bitte prüfen" where it was
 * unsure (with the source in the document), warnings, and the person's decision: change values, Übernehmen,
 * Ablehnen; once applied a link to the result and Rückgängig. "Direkt eingetragen" cards say so.
 * The card is the same for every tool; a tool adds only a small view (proposals/registry.ts) when it needs more.
 */
export function ProposalCard({ p, disabled }: { p: AgentProposalDto; disabled?: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useNotify();
  const [editing, setEditing] = useState(false);
  const [values, setValues] = useState<Record<string, FieldValue>>({});
  const [busy, setBusy] = useState<null | 'apply' | 'reject' | 'undo' | 'save'>(null);
  const [confirmUndo, setConfirmUndo] = useState(false);
  const open = p.status === 'pending' || p.status === 'failed';
  const editable = p.fields.filter((f) => f.type !== 'readonly');
  const view = viewFor(p.tool);
  const rows = view.hideFieldKeys?.length && !editing ? p.fields.filter((f) => !view.hideFieldKeys!.includes(f.key)) : p.fields;

  const run = async (kind: 'apply' | 'reject' | 'undo' | 'save') => {
    setBusy(kind);
    try {
      let next = p;
      if (kind === 'save' || (kind === 'apply' && editing && Object.keys(values).length)) {
        next = await api.agent.edit(p.id, values);
        setValues({});
        setEditing(false);
        upsertProposal(qc, next);
      }
      if (kind === 'apply') next = await api.agent.apply(p.id);
      if (kind === 'reject') next = await api.agent.reject(p.id);
      if (kind === 'undo') next = await api.agent.undo(p.id);
      upsertProposal(qc, next);
      if (kind === 'apply' || kind === 'undo') {
        await refreshData(qc);
        void qc.invalidateQueries({ queryKey: ['agent', 'guide'] });
      }
      // undo toast: one tap takes it back (the card keeps its own Rückgängig with confirmation)
      if (kind === 'apply' && next.status === 'applied') useAgentUi.getState().afterApply?.(next);
      if (kind === 'apply' && next.status === 'applied') toast.ok(next.undoable ? t('agent.undoToast') : t('agent.proposal.applied'), next.undoable ? { label: t('agent.proposal.undo'), run: () => void run('undo') } : undefined);
      if (kind === 'undo' && next.status === 'undone') toast.ok(t('agent.proposal.undone'));
    } catch (e) {
      toast.err(e);
    } finally {
      setBusy(null);
      setConfirmUndo(false);
    }
  };

  const statusLabel = p.status === 'applied' && p.auto ? t('agent.proposal.status.auto') : t(`agent.proposal.status.${p.status}`);
  return (
    <article className={`agent-proposal ${p.status}`} aria-label={p.title}>
      <header className="agent-proposal-head">
        <span className="agent-proposal-title">{p.title}</span>
        <Pill tone={TONE[p.status] ?? 'neutral'}>{statusLabel}</Pill>
      </header>
      {p.summary && <p className="agent-proposal-sum">{p.summary}</p>}
      {p.risk === 'external' && open && <p className="agent-proposal-note warn">{t('agent.proposal.external')}</p>}
      {p.warnings.length > 0 && open && (
        <ul className="agent-proposal-warn" role="list">
          {p.warnings.map((w) => <li key={w}><Icon name="alert" size={14} />{w}</li>)}
        </ul>
      )}
      <dl className="agent-proposal-fields">
        {rows.map((f) => (
          <div key={f.key} className={f.unsure && open ? 'unsure' : undefined}>
            <dt>{f.label}{f.unsure && open && <span className="agent-unsure">{t('agent.proposal.unsure')}</span>}</dt>
            <dd>
              {editing && f.type !== 'readonly' ? (
                <FieldInput f={f} value={values[f.key]} onChange={(v) => setValues((s) => ({ ...s, [f.key]: v }))} />
              ) : (
                showField(f, t)
              )}
              {f.source && <small className="agent-source">{t('agent.proposal.source', { source: f.source })}</small>}
            </dd>
          </div>
        ))}
      </dl>
      {view.Details && <view.Details p={p} open={open} />}
      {p.error && <p className="agent-proposal-note err" role="alert">{p.error}</p>}
      {p.status === 'applied' && p.auto && <p className="agent-proposal-note">{t('agent.proposal.autoNote')}</p>}
      {p.appliedAt && p.status !== 'pending' && <p className="agent-proposal-meta">{t('agent.proposal.appliedBy', { name: p.appliedByName ?? '', date: fmtDateTime(p.appliedAt) })}</p>}
      <footer className="agent-proposal-actions">
        {open && !editing && (
          <>
            <Button size="sm" loading={busy === 'apply'} disabled={disabled || !!busy} onClick={() => void run('apply')}>{t('agent.proposal.apply')}</Button>
            {editable.length > 0 && <Button size="sm" variant="secondary" disabled={disabled || !!busy} onClick={() => setEditing(true)}>{t('agent.proposal.edit')}</Button>}
            <Button size="sm" variant="ghost" loading={busy === 'reject'} disabled={disabled || !!busy} onClick={() => void run('reject')}>{t('agent.proposal.reject')}</Button>
          </>
        )}
        {open && editing && (
          <>
            <Button size="sm" loading={busy === 'apply'} disabled={!!busy} onClick={() => void run('apply')}>{t('agent.proposal.apply')}</Button>
            <Button size="sm" variant="secondary" loading={busy === 'save'} disabled={!!busy || !Object.keys(values).length} onClick={() => void run('save')}>{t('agent.proposal.save')}</Button>
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => { setEditing(false); setValues({}); }}>{t('agent.proposal.cancel')}</Button>
          </>
        )}
        {p.status === 'applied' && p.links.map((l) => <Link key={l.href} className="btn secondary sm" to={l.href}>{l.label}</Link>)}
        {p.undoable && <Button size="sm" variant="ghost" icon="refresh" loading={busy === 'undo'} disabled={!!busy} onClick={() => setConfirmUndo(true)}>{t('agent.proposal.undo')}</Button>}
      </footer>
      <ConfirmDialog open={confirmUndo} onClose={() => setConfirmUndo(false)} onConfirm={() => void run('undo')} title={t('agent.proposal.undoTitle')} text={t('agent.proposal.undoText')} label={t('agent.proposal.undo')} loading={busy === 'undo'} />
    </article>
  );
}

